
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";

interface Client {
  id: string;
  school_id: string;
  nama_sekolah: string;
  pordi_school_id?: string;
}

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientToDelete, setClientToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const { toast } = useToast();
  
  const debouncedSearch = useDebounce(searchQuery, 500);

  const fetchClients = async (search: string = "", currentPage: number = 1) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .rpc('get_filtered_clients', {
          search_query: search,
          page_number: currentPage,
          page_size: pageSize
        });

      if (error) throw error;

      if (data && data[0]) {
        const clientsData = (data[0].clients || []) as unknown as Client[];
        setClients(clientsData);
        setTotalCount(data[0].total_count);
      } else {
        setClients([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: "Error",
        description: "Failed to fetch clients",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchClients(searchQuery, newPage);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) {
        if (error.code === '23503') {
          toast({
            title: "Cannot Delete School",
            description: "This school has active subscriptions or bills and cannot be deleted.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      toast({
        title: "Success",
        description: "Client deleted successfully",
      });
      
      fetchClients(searchQuery, page);
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: "Error",
        description: "Failed to delete client",
        variant: "destructive",
      });
    } finally {
      setClientToDelete(null);
    }
  };

  useEffect(() => {
    fetchClients(debouncedSearch, page);
  }, [debouncedSearch]);

  return {
    clients,
    loading,
    clientToDelete,
    setClientToDelete,
    handleDelete,
    fetchClients,
    searchQuery,
    handleSearchChange,
    page,
    pageSize,
    totalCount,
    handlePageChange,
  };
};
